import {formatTimeObject} from '@utilities/date';

const initialState = () => {
    return {
        shortTimeHours: window.APP.orderShortTimeHours,

        searchParameters: null,
        travel: null,
        returnTravel: null,

        passengers: null,

        hasReturnTravel: false,

        addressId: 0,
        addresses: [],

        travelOptionPrices: null,
        travelOptionComments: null,

        purchaser: {
            purchaser_email: null,
            purchaser_first_name: null,
            purchaser_last_name: null,
            purchaser_phone: null,
        },

        isInvoice: false,
        invoice: {
            email: null,
            company: null,
            vat_number: null,
            address: null,
            zip: null,
            city: null,
            country: null,
        },

        discount: null,
        isCheckedCreateAccount: false,
        password: null,
        passwordConfirmation: null,

        showPassengersNumber: 3,

        startPlaces: null,
        endPlaces: null,
        payment_provider: 'p24',
    };
};

const state = initialState;

const getters = {
    searchParameters: (state) => {
        return state.searchParameters;
    },

    getTravel: (state) => {
        return state.travel;
    },

    getReturnTravel: (state) => {
        return state.returnTravel;
    },

    getPassengers: (state) => {
        return state.passengers;
    },

    hasReturnTravel: (state) => {
        return state.hasReturnTravel;
    },

    getAddresses: (state) => (type) => {
        if (type) {
            return state.addresses.filter((address) => address.type === type);
        }

        return state.addresses;
    },

    hasAddresses: (state) => {
        return state.addresses.length > 0;
    },

    getTravelOptionPrices: (state) => {
        return state.travelOptionPrices;
    },

    getTravelOptionComments: (state) => {
        return state.travelOptionComments;
    },

    getIsInvoice: (state) => {
        return state.isInvoice;
    },

    getInvoice: (state) => {
        return state.invoice;
    },

    getPurchaser: (state) => {
        return state.purchaser;
    },

    getDiscount: (state) => {
        return state.discount;
    },

    getCartBaseTotal: (state) => {
        let travelTotal = state.travel.gross_price;
        let returnTotal = 0;
        let optionsTotal = 0;

        if (state.travelOptionPrices) {
            optionsTotal = state.travelOptionPrices
                .map((option) => option.gross_price * option.quantity)
                .reduce((a, b) => a + b, 0);
        }

        if (state.hasReturnTravel && state.returnTravel) {
            optionsTotal *= 2;
            returnTotal = state.returnTravel.gross_price;
        }

        return travelTotal + returnTotal + optionsTotal;
    },

    getCartDiscountGrossValue: (state, getters, rootState, rootGetters) => {
        if (!state.discount) {
            return 0;
        }

        if (state.discount.discount_type === rootGetters['constants/discountTypePercent']) {
            return getters.getCartBaseTotal * (state.discount.discount_percent / 100);
        }

        if (state.discount.discount_gross_value > getters.getCartBaseTotal) {
            return getters.getCartBaseTotal;
        }

        return state.discount.discount_gross_value;
    },

    getUserDiscountGrossValue: (state, getters, rootState) => {
        if (!rootState.user.discount_percent) {
            return 0;
        }

        return (rootState.user.discount_percent / 100) * (getters.getCartBaseTotal - getters.getCartDiscountGrossValue);
    },

    getCartTotal: (state, getters) => {
        return getters.getCartBaseTotal - getters.getCartDiscountGrossValue - getters.getUserDiscountGrossValue;
    },


    getCartData: (state) => {
        let cartData = {
            offer_type: 'fat',

            travel_id: state.travel.id,
            return_travel_id: state.returnTravel ? state.returnTravel.id : null,
            promo_price_id: state.travel.promo_price_id ? state.travel.promo_price_id : null,
            return_promo_price_id: state.returnTravel && state.returnTravel.promo_price_id ? state.returnTravel.promo_price_id : null,
            discount_code_id: state.discount ? state.discount.id : null,

            departure_date: `${state.searchParameters.departure_date} ${state.searchParameters.departure_time}`,
            has_return_travel: state.hasReturnTravel,

            passengers: state.passengers,
            passenger_quantities: state.searchParameters.passengers,

            addresses: state.addresses,

            travel_options: state.travelOptionPrices,
            travel_options_comments: state.travelOptionComments,

            ...state.purchaser,

            is_invoice: state.isInvoice,
            invoice: state.isInvoice ? state.invoice : null,

            create_account: state.isCheckedCreateAccount,
            password: state.password,
            password_confirmation: state.passwordConfirmation,
            payment_provider: state.payment_provider,
        };

        if (state.searchParameters.return_date) {
            let returnTime = '';

            if (state.searchParameters.return_time) {
                returnTime = `${formatTimeObject(state.searchParameters.return_time)}`;
            } else {
                returnTime = '00:00';
            }

            cartData['return_departure_date'] = `${state.searchParameters.return_date} ${returnTime}`;
        }

        return cartData;
    },

    isCheckedCreateAccount: (state) => {
        return state.isCheckedCreateAccount;
    },

    showPassengersNumber: (state) => {
        return state.showPassengersNumber;
    },

    additionalOptionsTotal: (state, getters) => {
        if (!getters.travelOptionPrices) {
            return 0;
        }

        return getters.travelOptionPrices.reduce(function(previous, current){
            return previous + (current.gross_price * current.quantity);
        }, 0);
    },

    passengersNumber: (state) => {
        let total = 0;

        if (state.searchParameters && state.searchParameters.passengers) {
            state.searchParameters.passengers.forEach((passenger) => {
                total += passenger.quantity;
            });
        }

        return total;
    },

    startPlaces: (state) => {
        return state.startPlaces;
    },

    endPlaces: (state) => {
        return state.endPlaces;
    },
};

const mutations = {
    setSearchParameters(state, parameters) {
        state.searchParameters = parameters;
    },

    setTravel(state, travel) {
        if (travel.promo_price) {
            travel.promo_price_id = travel.promo_price.promo_price_id;
            travel.net_price = travel.promo_price.net_price;
            travel.gross_price = travel.promo_price.gross_price;
            travel.vat = travel.promo_price.vat;

            delete travel.promo_price;
        }

        state.travel = travel;
    },

    setReturnTravel(state, returnTravel) {
        if (!returnTravel) {
            state.returnTravel = null;
            return;
        }

        if (returnTravel.promo_price) {
            returnTravel.promo_price_id = returnTravel.promo_price.promo_price_id;
            returnTravel.net_price = returnTravel.promo_price.net_price;
            returnTravel.gross_price = returnTravel.promo_price.gross_price;
            returnTravel.vat = returnTravel.promo_price.vat;

            delete returnTravel.promo_price;
        }

        state.returnTravel = returnTravel;
    },

    setPassengers(state, passengers) {
        state.passengers = passengers;
    },

    setHasReturnTravel(state, bool) {
        state.hasReturnTravel = bool;
    },

    setAddressId(state, id) {
        state.addressId = id;
    },

    addAddress(state, address) {
        state.addresses.push(address);
    },

    updateAddress(state, data) {
        const index = state.addresses.indexOf(
            state.addresses.find((address) => address.id === data.id)
        );

        state.addresses.splice(index, 1, data);
    },

    removeAddress(state, id) {
        const idx = state.addresses.indexOf(
            state.addresses.find((address) => address.id === id)
        );

        state.addresses.splice(idx, 1);
    },

    setTravelOptionPrices(state, options) {
        state.travelOptionPrices = options;
    },

    setTravelOptionComments(state, comments) {
        state.travelOptionComments = comments;
    },

    setIsInvoice(state, bool) {
        state.isInvoice = bool;
    },

    setInvoice(state, invoice) {
        state.invoice = invoice;
    },

    setPurchaser(state, parameters) {
        state.purchaser = parameters;
    },

    setDiscount(state, discount) {
        state.discount = discount;
    },

    setIsCheckedCreateAccount(state, value) {
        state.isCheckedCreateAccount = value;
    },

    setPassword(state, password) {
        state.password = password;
    },

    setPasswordConfirmation(state, password) {
        state.passwordConfirmation = password;
    },

    increaseShownPassengersNumber(state) {
        state.showPassengersNumber++;
    },

    setStartPlaces(state, data) {
        state.startPlaces = data;
    },

    setEndPlaces(state, data) {
        state.endPlaces = data;
    },

    setPaymentMethod(state, paymentMethod) {
        state.payment_provider = paymentMethod;
    },

    reset(state) {
        const initial = initialState();

        Object.keys(initial).forEach((key) => {
            state[key] = initial[key];
        });
    },
};

const actions = {
    setSearchParameters({ commit }, parameters) {
        commit('setSearchParameters', parameters);
    },

    setSearchParameter({ commit, state }, payload) {
        let parameters = Object.assign({}, state.searchParameters);
        parameters[payload.field] = payload.value;

        commit('setSearchParameters', parameters);
    },

    setTravel({ commit }, travel) {
        commit('setTravel', travel);
    },

    setReturnTravel({ commit }, returnTravel) {
        commit('setReturnTravel', returnTravel);
    },

    setPassengers({ commit }, passengers) {
        commit('setPassengers', passengers);
    },

    addAddress({ commit, state }, address) {
        address.id = state.addressId + 1;

        commit('setAddressId', address.id);
        commit('addAddress', address);
    },

    updateAddress({ commit }, payload) {
        commit('updateAddress', payload);
    },

    removeAddress({ commit }, id) {
        commit('removeAddress', id);
    },

    removeAddressByType({ commit, getters }, type) {
        getters.getAddresses(type).forEach((address) => {
            commit('removeAddress', address.id);
        });
    },

    setHasReturnTravel({ commit }, bool) {
        commit('setHasReturnTravel', bool);
    },

    setTravelOptionPrices({ commit }, options) {
        commit('setTravelOptionPrices', options);
    },

    setTravelOptionComments({commit}, comments) {
        commit('setTravelOptionComments', comments);
    },

    setIsInvoice({ commit }, bool) {
        commit('setIsInvoice', bool);
    },

    setInvoice({ commit }, invoice) {
        commit('setInvoice', invoice);
    },

    setPurchaser({ commit }, parameters) {
        commit('setPurchaser', parameters);
    },

    setDiscount({ commit }, discount) {
        commit('setDiscount', discount);
    },

    setIsCheckedCreateAccount({ commit }, value) {
        commit('setIsCheckedCreateAccount', value);
    },

    setPassword({ commit }, password) {
        commit('setPassword', password);
    },

    setPasswordConfirmation({ commit }, password) {
        commit('setPasswordConfirmation', password);
    },

    increaseShownPassengersNumber({ commit }) {
        commit('increaseShownPassengersNumber');
    },

    setStartPlaces({ commit }, data) {
        commit('setStartPlaces', data);
    },

    setEndPlaces({ commit }, data) {
        commit('setEndPlaces', data);
    },

    setPaymentMethod({ commit}, paymentMethod) {
        commit('setPaymentMethod', paymentMethod);
    },

    reset({ commit }) {
        commit('reset');
    },
};


export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
